/**
 * WooCommerce Blocks / Interactivity API DOM bridge
 *
 * WooCommerce is migrating its interactive blocks from React to the
 * Interactivity API. Product Collection, Product Filters, Product Button,
 * Product Gallery, Add to Cart with Options and the Mini Cart already run on
 * it. Two consequences matter for tracking:
 *
 * 1. Those blocks re-render regions of the page client-side. The Interactivity
 *    Router replaces the content of a data-wp-router-region on pagination and
 *    filtering, so every listener the Pixel Manager bound directly to an
 *    element at DOM ready is lost with the elements it was bound to, and every
 *    product that arrives that way is invisible to the intersection observer.
 *    WordPress emits no public event for this (state.navigation.hasFinished is
 *    deprecated as of WP 7.1), so the only durable signal is the DOM itself.
 *
 * 2. Product Collection items are not printed through the classic loop hooks.
 *    WooCommerce only routes woocommerce_after_shop_loop_item into them through
 *    its archive template compatibility layer, and only for collections that
 *    inherit the template query. Everything else (a collection on a page, a
 *    curated collection, and anything the router loads afterwards) reaches the
 *    browser without a .pmwProductId marker.
 *
 * This file provides the shared machinery for both: a debounced DOM refresh
 * pipeline other listener files hook into, an idempotent binding helper, and
 * client-side marker injection derived from the block's own item key.
 *
 * @since 1.65.0
 */

(function (pmw, $, undefined) {

	/**
	 * This file loads alongside the platform layer, before pmw.console is
	 * wired up in functions_loader.js, so the error path cannot assume it.
	 *
	 * @param {...*} args
	 */
	const logError = (...args) => {

		if (pmw.console && typeof pmw.console.error === "function") {
			pmw.console.error(...args);
			return;
		}

		console.error(...args);
	};

	/**
	 * Callbacks that re-scan the DOM after it changed.
	 *
	 * @type {Function[]}
	 */
	const refreshCallbacks = [];

	/**
	 * Selectors the refresh callbacks act on, used to tell a DOM change that
	 * concerns tracking apart from the constant churn of everything else on a
	 * shop page: chat widgets, lazy loaded images, sliders, cookie banners.
	 *
	 * Without this the observer below would schedule a refresh for every one
	 * of those, and each refresh re-runs every selector over the whole page.
	 *
	 * @type {string[]}
	 */
	const watchedSelectors = [];

	let watchedSelector = "";

	/**
	 * Register a callback that runs whenever relevant elements are added to
	 * the page.
	 *
	 * Callbacks must be idempotent: they run again on every DOM change, and a
	 * callback that emits an event or binds a duplicate listener on each pass
	 * would multiply tracking.
	 *
	 * @param {Function} callback
	 * @param {string}   [selector] What the callback acts on. Without it the callback runs on every relevant change.
	 */
	pmw.onDomRefresh = (callback, selector = "") => {

		if (typeof callback !== "function") return;

		refreshCallbacks.push(callback);

		if (selector) {
			watchedSelectors.push(selector);
			watchedSelector = watchedSelectors.join(", ");
		}
	};

	/**
	 * Whether an added element is one the refresh callbacks care about, itself
	 * or anywhere below it. Scoped to the added subtree, so the cost is bound
	 * to the size of what changed rather than to the size of the page.
	 *
	 * @param {Element} element
	 * @returns {boolean}
	 */
	const isWatchedElement = element => {

		if (!watchedSelector) return false;

		try {
			return element.matches(watchedSelector) || !!element.querySelector(watchedSelector);
		} catch (e) {
			return false;
		}
	};

	let refreshTimer   = null;
	let refreshRunning = false;

	/**
	 * Run all registered refresh callbacks, debounced.
	 *
	 * Block re-renders arrive as bursts of mutations, so the callbacks run once
	 * the DOM has settled rather than per mutation record.
	 */
	pmw.triggerDomRefresh = () => {

		clearTimeout(refreshTimer);

		refreshTimer = setTimeout(() => {

			refreshRunning = true;

			refreshCallbacks.forEach(callback => {
				try {
					callback();
				} catch (e) {
					logError(e);
				}
			});

			// The callbacks mutate the DOM themselves (marker injection), which
			// would otherwise feed straight back into the observer below. The
			// mutation records are delivered as a microtask, so releasing the
			// flag in a macrotask reliably swallows our own writes. The
			// callbacks are idempotent either way, this only avoids the extra
			// pass.
			setTimeout(() => {
				refreshRunning = false;
			}, 0);

		}, 250);
	};

	/**
	 * Elements already bound per binding key, so re-binding after a re-render
	 * can never attach a second handler to a surviving element.
	 *
	 * @type {Object.<string, WeakSet>}
	 */
	const boundElements = {};

	/**
	 * Bind a handler to every element matching a selector, at most once per
	 * element.
	 *
	 * Delegating everything to document is not an option here: the Pixel
	 * Manager's add-to-cart selector list is deliberately element-scoped
	 * because a document or body level handler sees the same click through
	 * every matching ancestor and fires the event twice. Binding per element
	 * and remembering which elements are already bound keeps the existing
	 * semantics while surviving re-renders.
	 *
	 * @param {string}   key      Identifies the binding, so different handlers on the same element stay independent.
	 * @param {string}   selector
	 * @param {string}   events   Space separated jQuery event names.
	 * @param {Function} handler
	 * @param {Object}   [options]
	 * @param {boolean}  [options.once] Bind with .one() instead of .on().
	 */
	pmw.bindOnce = (key, selector, events, handler, options = {}) => {

		try {

			if (!boundElements[key]) boundElements[key] = new WeakSet();

			const bound = boundElements[key];

			jQuery(selector).each((index, element) => {

				if (bound.has(element)) return;

				bound.add(element);

				if (options.once) {
					jQuery(element).one(events, handler);
				} else {
					jQuery(element).on(events, handler);
				}
			});

		} catch (e) {
			logError(e);
		}
	};

	/**
	 * Bind now and re-bind after every DOM refresh.
	 *
	 * @param {string}   key
	 * @param {string}   selector
	 * @param {string}   events
	 * @param {Function} handler
	 * @param {Object}   [options]
	 */
	pmw.bindAndRebind = (key, selector, events, handler, options = {}) => {

		const bind = () => pmw.bindOnce(key, selector, events, handler, options);

		bind();
		pmw.onDomRefresh(bind, selector);
	};

	/**
	 * Product Collection renders every product as
	 *
	 *   <li class="wc-block-product post-123 product type-product ..."
	 *       data-wp-key="product-item-123">
	 *
	 * The key attribute is the block's own identity for the item, which makes
	 * it the most reliable place to read the product id from: it is what the
	 * Interactivity API diffs on, so it is present on server-rendered and
	 * router-loaded items alike, including in the markup the router swaps in.
	 *
	 * @type {string}
	 */
	const productCollectionItemSelector = "[data-wp-key^='product-item-']";

	/**
	 * Inject the .pmwProductId marker into every Product Collection item that
	 * does not have one yet.
	 *
	 * The marker is what view_item_list and select_item resolve products
	 * through, and the intersection observer looks for it inside the watched
	 * element, so appending it to the item is enough.
	 *
	 * @returns {string[]} Ids of newly marked products whose data is not in the data layer yet.
	 */
	pmw.injectProductCollectionMarkers = () => {

		const missingProductIds = [];

		try {

			jQuery(productCollectionItemSelector).each((index, element) => {

				const $element = jQuery(element);

				if ($element.find(".pmwProductId").length) return;

				const productId = String($element.attr("data-wp-key") || "").replace("product-item-", "");

				if (!productId) return;

				$element.append(
					jQuery("<input>", {
						type   : "hidden",
						class  : "pmwProductId",
						"data-id": productId,
					})
				);

				if (!pmwDataLayer?.products?.[productId]) missingProductIds.push(productId);
			});

		} catch (e) {
			logError(e);
		}

		return missingProductIds;
	};

	/**
	 * Bring products that appeared client-side into tracking.
	 *
	 * The Interactivity Router replaces only the router region, so the inline
	 * data layer scripts the Pixel Manager prints outside that region never run
	 * for router-loaded products. Their data has to come from the backend.
	 */
	const refreshProductCollection = async () => {

		try {

			const missingProductIds = pmw.injectProductCollectionMarkers();

			if (missingProductIds.length) {
				await pmw.getProductsFromBackend(missingProductIds);
			}

		} catch (e) {
			logError(e);
		}
	};

	pmw.onDomRefresh(refreshProductCollection, productCollectionItemSelector);

	/**
	 * Hand the products that just appeared to the impression observer. Runs
	 * after the marker injection above, which is what it resolves them
	 * through, and covers products from any source, not only blocks.
	 */
	pmw.onDomRefresh(() => {

		if (typeof pmw.observeNewProductElements === "function") {
			pmw.observeNewProductElements();
		}

	}, ".pmwProductId");

	let domObserver = null;

	/**
	 * Start watching the document for client-side rendered content.
	 *
	 * Scoping the observer to the router regions would miss blocks that
	 * re-render in place without a navigation (Product Filters, the Mini Cart
	 * drawer, Add to Cart with Options), so it watches the body. The callback
	 * only schedules the debounced refresh, all real work happens once per
	 * burst.
	 */
	pmw.startInteractivityDomObserver = () => {

		try {

			if (domObserver) return;
			if (typeof MutationObserver === "undefined") return;
			if (!document.body) return;

			domObserver = new MutationObserver(mutations => {

				if (refreshRunning) return;

				for (let i = 0; i < mutations.length; i++) {

					const addedNodes = mutations[i].addedNodes;

					if (!addedNodes || !addedNodes.length) continue;

					for (let j = 0; j < addedNodes.length; j++) {

						if (addedNodes[j].nodeType !== Node.ELEMENT_NODE) continue;

						if (isWatchedElement(addedNodes[j])) {
							pmw.triggerDomRefresh();
							return;
						}
					}
				}
			});

			domObserver.observe(document.body, {
				childList: true,
				subtree  : true,
			});

			// Server-rendered Product Collection items that the classic loop
			// hooks never reached are handled by the same pass as router
			// loaded ones.
			pmw.triggerDomRefresh();

		} catch (e) {
			logError(e);
		}
	};

}(window.pmw = window.pmw || {}, jQuery));
